import { ApolloError } from '@apollo/client';

import { useStudentDetailQuery, StudentDetailQuery } from 'core/graphql';

/** Narrow projection used by the StudentDetail page Overview tab. */
export type IStudentDetail = NonNullable<StudentDetailQuery['student']>;

interface IStudentDetailHook {
  student?: IStudentDetail;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * The backend's `student(id:)` resolver is Relay-style — it expects a
 * base64 global ID like `U3R1ZGVudFR5cGU6MjI=` (`StudentType:22`),
 * not the bare PK. Navigation from the students table already passes
 * the Relay ID (it's `node.id` straight from `PaginatedStudents`), but
 * a hand-typed URL like `/students/22/details` would otherwise
 * silently return `data.student === null`. Encode bare digits on the fly
 * so both URL forms resolve to the same record.
 */
export const toStudentGlobalId = (raw: string): string => {
  if (/^\d+$/.test(raw)) {
    // btoa is available in every browser CRA targets; React Native isn't a
    // shipping platform for this app, so no polyfill needed.
    return btoa(`StudentType:${raw}`);
  }
  return raw;
};

/** Backs `/students/:id/details` — `student(id)` resolver, projection from
 *  `entityDetails.ts`. `errorPolicy: 'all'` so a single sub-field error on the
 *  server (e.g. a transient resolver issue on `agreement.payments`) doesn't
 *  blank the whole page — Apollo keeps the partial `data` and we render the
 *  banner alongside whatever it did manage to fetch. */
export const useStudentDetail = (id?: string): IStudentDetailHook => {
  const { data, loading, error, refetch } = useStudentDetailQuery({
    variables: { id: id ? toStudentGlobalId(id) : '' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  return {
    student: data?.student ?? undefined,
    loading,
    error,
    refetch: () => {
      refetch();
    },
  };
};
